import React from 'react';
import { SONG_SLOT_MAX_LENGTH } from '@soundxyz/utils/constants/constants';
import { getCommentTimeStamp } from '@soundxyz/utils/helpers';
import { styled } from '@components-shared/src/stitches';
import { PlayerCommentCard } from '@components/CommentCard/PlayerCommentCard';
import type { UserCommentProps } from '@components/Waveform/AvatarWithComment';
import { bubble } from '@components/animations/bubble';
import { HoverCard } from '@components/main-components/hover-card';
import { getFragment, WaveformCommentInfoFragmentDoc } from '@graphql/reactQuery';
import { getCommentPosition } from '@utils/helpers';

export const UserComment = React.memo(function AvatarWithComment({
  nft: nftFrag,
  release,
  width,
  isOpen,
}: UserCommentProps) {
  const nft = getFragment(WaveformCommentInfoFragmentDoc, nftFrag);

  const commentTimestamp = getCommentTimeStamp({
    songSlot: nft.songSlot,
    totalSlots: SONG_SLOT_MAX_LENGTH,
    trackDuration: release.track.duration,
  });

  const comment = {
    id: nft.id,
    avatarUrl: nft.avatarUrl,
    commentText: nft.comment.message,
    publicAddress: nft.ownerPublicAddress,
    username: nft.ownerUsername,
    trackDuration: release.track.duration,
    updatedAt: nft.updatedAt,
    isArtist: !!nft.ownerArtistName,
    webappUri: nft.ownerWebappUri,
    isEggWinner: nft.isGoldenEgg,
  };

  const commentPosition = getCommentPosition({
    availableWidth: width,
    avatarSize: 6,
    commentTimestamp,
    trackDuration: release?.track?.duration,
  });

  return (
    <HoverCard key={nft.id} open={isOpen}>
      <AvatarContainer
        css={{
          left: `${commentPosition}px`,
        }}
        key={nft.id}
      >
        <HoverContent side="top" sideOffset={112}>
          <PlayerCommentCard comment={comment} />
        </HoverContent>
        <StyledTrigger asChild>
          <PinkDot />
        </StyledTrigger>
      </AvatarContainer>
    </HoverCard>
  );
});

const AvatarContainer = styled('div', {
  pointerEvents: 'all',
  position: 'absolute',
  cursor: 'pointer',
});

const StyledTrigger = styled(HoverCard.Trigger, {
  outline: 'none',

  '&:hover, &:focus, &:focus': {
    outline: 'none',
  },
});

const PinkDot = styled('span', {
  display: 'flex',
  flexShrink: 0,
  width: '4px ',
  height: '4px ',
  backgroundColor: '$fuchsia500',
  border: '1px solid #000',

  '&:hover, &:focus, &:focus': {
    backgroundColor: '$fuchsia500',
    outline: 'none',
  },
});

const HoverContent = styled(HoverCard.Content, {
  zIndex: '$audioPlayerComments',
  width: '100vw',

  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${bubble} 300ms ease-in`,
  },
});
